import {Domain, Step} from '@effector/effector'
import {Node, Edge} from '../workingSet'

const queryTypes = {
  store: {unit: 'store'},
  effect: {unit: 'effect'},
  fxRunner: {op: 'fx', fx: 'runner'},
  event: {unit: 'event'},
  watch: {op: 'watch'},
  on: {op: 'on'},
  sampleStore: {op: 'sample', sample: 'store'},
  sampleSource: {op: 'sample', sample: 'source'},
  sampleClock: {op: 'sample', sample: 'clock'}
}
type UnitGroups = {
  [K in keyof typeof queryTypes]: {node: Step; id: string}[]
} & {
  forward: Array<{
    node: Step
    from: Step
    to: Step
    ids: {
      node: string
      from: string
      to: string
    }
  }>
}
type UnitHandlers = {
  [K in keyof UnitGroups]: (
    opts: UnitGroups[K] extends Array<infer Arg> ? Arg : never
  ) => void
}
export function graphParser(
  root: Domain,
  handlers: Partial<UnitHandlers> = {}
) {
  const nodes: Node[] = []
  const edges: Edge[] = []
  //@ts-ignore
  const unitList = flatGraph(root.graphite)
  const unitGroups: UnitGroups = {
    store: [],
    effect: [],
    fxRunner: [],
    event: [],
    watch: [],
    on: [],
    sampleStore: [],
    sampleSource: [],
    sampleClock: [],
    forward: []
  }
  const groupHandlers: UnitHandlers = {
    store: ({node, id}) => {
      nodes.push({
        id,
        label: node.meta.name,
        node,
        unit: 'store',
        exclude: isExcluded(node)
      })
    },
    effect: ({node, id}) => {
      node.scope.runner.meta.name = node.meta.name
      nodes.push({
        id,
        label: node.meta.name,
        node,
        unit: 'effect',
        exclude: isExcluded(node)
      })
    },
    fxRunner: ({node, id}) => {
      const effectNode = node.family.owners.find(e => e.meta.unit === 'effect')!
      const doneNode = effectNode.family.links.find(
        e => e.meta.named === 'done'
      )!
      const failNode = effectNode.family.links.find(
        e => e.meta.named === 'fail'
      )!
      const finallyNode = effectNode.family.links.find(
        e => e.meta.named === 'finally'
      )!
      doneNode.meta.name = `${effectNode.meta.name}.done`
      failNode.meta.name = `${effectNode.meta.name}.fail`
      finallyNode.meta.name = `${effectNode.meta.name}.finally`
    },
    event: ({node, id}) => {
      nodes.push({
        id,
        label: node.meta.name,
        node,
        unit: 'event',
        exclude: isExcluded(node)
      })
    },
    watch: ({node, id}) => {
      const owner = node.family.owners[0]
      nodes.push({
        id,
        label: 'watch',
        node,
        op: 'watch',
        owner: getID(owner),
        sources: node.scope.fn.toString(),
        exclude: isExcluded(node) || isExcluded(owner)
      })
    },
    on: ({node, id}) => {
      const [clock, source] = node.family.owners
      nodes.push({
        id,
        label: 'on',
        node,
        op: 'on',
        source: getID(source),
        clock: getID(clock),
        sources: node.scope.fn.toString(),
        exclude: isExcluded(node) || isExcluded(source)
      })
    },
    sampleStore: ({node, id}) => {
      const [clock, target, source] = node.family.owners
      nodes.push({
        id,
        label: 'sample',
        node,
        op: 'sample',
        source: getID(source),
        clock: getID(clock),
        target: getID(target),
        exclude: isExcluded(node)
      })
    },
    sampleSource: ({node, id}) => {
      console.log('sample source', node)
    },
    sampleClock: ({node, id}) => {
      console.log('sample clock', node)
    },
    forward: ({node, from, to, ids}) => {
      nodes.push({
        id: ids.node,
        label: 'forward',
        node,
        op: 'forward',
        source: ids.from,
        target: ids.to,
        exclude: isExcluded(to)
      })
    }
  }
  for (const node of unitList) {
    const id = getID(node)
    for (const queryType in queryTypes) {
      if (matchShape(queryTypes[queryType], node)) {
        unitGroups[queryType].push({
          node,
          id
        })
      }
    }
    for (const next of node.next) {
      if (next.meta.op !== 'forward') continue
      unitGroups.forward.push({
        node: next,
        from: node,
        to: next.next[0],
        ids: {
          node: getID(next),
          from: id,
          to: getID(next.next[0])
        }
      })
    }
  }
  for (const key in unitGroups) {
    const group = unitGroups[key]
    const handler = groupHandlers[key]
    for (const item of group) {
      handler(item)
    }
    if (key in handlers) {
      for (const item of group) {
        handlers[key](item)
      }
    }
  }

  return {
    nodes,
    edges
  }

  function matchShape(shape, unit) {
    for (const key in shape) {
      if (unit.meta[key] !== shape[key]) return false
    }
    return true
  }
  function getID(node): string {
    const id = unitList.indexOf(node)
    if (id === -1) throw Error('not found')
    return '' + id
  }
}
function isExcluded(node) {
  return !!node.meta.named
}
function flatGraph(unit): Step[] {
  const visited = new Set<any>()
  traverse(unit.graphite || unit)
  function traverse(node) {
    if (visited.has(node)) return
    visited.add(node)
    node.next.forEach(traverse)
    node.family.owners.forEach(traverse)
    node.family.links.forEach(traverse)
  }
  return [...visited]
}
